import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { renderPartnerLogo, partnersContent } from './utils';

const OrganizersSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Organizers
  const organizers = [
    {
      id: 1,
      name: "منبر منظمات المجتمع المدني",
      logo: "/assets/logos/ULFED.png",
      logoAlt: "ULFED - Uluslararası STK Federasyonu logo",
      website: "http://ulfed.org/ar",
      about: {
        ar: "منبر منظمات المجتمع المدني ULFED، اتحاد غير ربحي مستقل، وكيان قانوني مسجل في “المديرية العامة لإدارة العلاقات مع المجتمع المدني” التركية، يضم مؤسسات المجتمع المدني سورية الأصل-تركية المنشأ. يعمل على تطوير انسجام مؤسسات المجتمع المدني السورية ضمن المحيط التركي. وذلك عبر تعزيز عملية التواصل، ودعم بناء القدرات، وتطوير البيئة القانونية.",
        en: "International NGO Federation - A platform that brings together civil society organizations and enhances cooperation and coordination among them to achieve sustainable development goals and serve local communities.",
        tr: "Uluslararası STK Federasyonu - Sivil toplum kuruluşlarını bir araya getiren ve sürdürülebilir kalkınma hedeflerine ulaşmak ve yerel topluluklara hizmet etmek için aralarında işbirliği ve koordinasyonu artıran bir platform."
      }
    },
    {
      id: 2,
      name: "الإتحاد العام لطلبة سوريا",
      logo: "/assets/logos/guofss_logo.png",
      logoAlt: "GUOFSS logo",
      website: "http://guofsyrians.org",
      about: {
        ar: "هيئة طلابية نقابية وطنية منتخبة ومستقلة، تهدف لتمثيل الحركة الطلابية السورية داخل الجامعات التركية، تستمد شرعيتها من قواعدهـا الطلابيـة الجامعيـة، و تمثل الطلبـة السوريين بقضاياهم وحقوقهم في تركيا.",
        en: "A national, elected, and independent student union body that aims to represent the Syrian student movement within Turkish universities. It derives its legitimacy from its university student base and represents Syrian students with their issues and rights in Turkey.",
        tr: "Türk üniversitelerindeki Suriye öğrenci hareketini temsil etmeyi amaçlayan ulusal, seçilmiş ve bağımsız bir öğrenci sendika organıdır. Meşruiyetini üniversite öğrenci tabanından alır ve Suriye öğrencilerini Türkiye'deki sorunları ve haklarıyla temsil eder."
      }
    }
  ];

  const content = partnersContent?.[currentLanguage];

  return (
    <section id="who-are-we" className="relative py-16 lg:py-24 px-4 bg-gradient-to-b from-[#114845]/5 via-[#114845]/3 to-background overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto">
        {/* Who are we Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20 lg:mb-24"
        >
          <div className="relative bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-8 md:p-12 shadow-memorial overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-accent/5 rounded-full blur-2xl translate-x-1/2 translate-y-1/2"></div>
            
            <div className="relative z-10 text-center">
              {/* Decorative Icon Above Title */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex justify-center mb-6"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/10 rounded-full blur-xl"></div>
                  <div className="relative w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center border border-primary/30 shadow-memorial">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </motion.div>

              <h2 className="font-memorial text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4">
                <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent drop-shadow-sm">
                  {content?.whoAreWeTitle}
                </span>
              </h2>
              
              {/* Enhanced Decorative Underline */}
              <motion.div
                initial={{ opacity: 0, scaleX: 0 }}
                whileInView={{ opacity: 1, scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex items-center justify-center gap-3 mt-8"
              >
                <div className="h-1.5 w-16 bg-gradient-to-r from-transparent via-primary/50 to-primary rounded-full"></div>
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-full blur-sm"></div>
                  <div className="relative w-3 h-3 bg-primary rounded-full shadow-sm"></div>
                </div>
                <div className="h-1.5 w-8 bg-primary rounded-full"></div>
                <div className="relative">
                  <div className="absolute inset-0 bg-accent/20 rounded-full blur-sm"></div>
                  <div className="relative w-3 h-3 bg-accent rounded-full shadow-sm"></div>
                </div>
                <div className="h-1.5 w-16 bg-gradient-to-l from-transparent via-accent/50 to-accent rounded-full"></div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0 }}
          className="mb-24 lg:mb-32"
        >
        <div className="space-y-12 max-w-4xl mx-auto">
          {organizers.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              className="flex flex-col items-center text-center"
            >
              {item.logo && item.logo !== "" ? (
                <>
                  <div className="flex items-center justify-center mb-3 h-32">
                    {renderPartnerLogo(item)}
                  </div>
                  <h4 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6">
                    {item.name}
                  </h4>
                </>
              ) : (
                <div className="text-2xl md:text-3xl font-semibold mb-6">{item.name}</div>
              )}
              {item.about && item.about[currentLanguage] && (
                <p
                  className="text-base md:text-lg text-gray-700 leading-relaxed max-w-3xl"
                  dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
                >
                  {item.about[currentLanguage]}
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Coordination Section */}
        <div className="mt-24 flex flex-col items-center justify-center">
          <h3 className="font-memorial text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-center">
            <span className="bg-gradient-to-r from-primary via-accent to-success bg-clip-text text-transparent drop-shadow-sm">
              بالتنسيق مع إدارة شؤون المغتربين
            </span>
          </h3>
          
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center justify-center gap-2 mb-8"
          >
            <div className="h-1 w-12 bg-gradient-to-r from-transparent to-primary rounded-full"></div>
            <div className="h-1 w-2 bg-primary rounded-full"></div>
            <div className="h-1 w-12 bg-gradient-to-l from-transparent to-primary rounded-full"></div>
          </motion.div>

          <div className="w-full max-w-xs flex justify-center">
             {renderPartnerLogo({
               name: "إدارة شؤون المغتربين",
               logo: "/assets/logos/ministry.png",
               logoAlt: "مديرية شؤون المغتربين",
               website: "https://mofaex.gov.sy/",
               className: "max-h-68 md:max-h-96"
             })}
          </div>
        </div>
      </motion.div>
      </div>
    </section>
  );
};

export default OrganizersSection;

